import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { SuggestList, fillLast, useSuggest } from "@/components/common/SuggestInput";

export function useDebounced(value, ms = 220) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export default function SearchPicker({ placeholder, fetcher, render, onPick, onEnterExact, inputRef, testid }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const dq = useDebounced(q);
  const seq = useRef(0);
  const blurT = useRef(null);
  const own = useRef(null);
  const ref = inputRef || own;
  const sug = useSuggest(q, "products", fetcher === fetchProducts);  // v1.30.1 kelime önerileri (yalnız ürün araması)
  const [sa, setSa] = useState(-1);
  useEffect(() => setSa(-1), [sug]);
  const fill = (s) => { setQ(fillLast(q, s.text)); setSa(-1); };
  useEffect(() => {
    if (dq.trim().length < 2) { seq.current += 1; setItems([]); return; }
    const my = ++seq.current;
    fetcher(dq.trim()).then((r) => { if (my === seq.current) { setItems(r); setActive(0); if (document.activeElement === ref.current) setOpen(true); } });
  }, [dq, fetcher]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (it) => { onPick(it); setQ(""); setItems([]); setOpen(false); };
  const onKey = async (e) => {
    if ((e.altKey || items.length === 0) && sug.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) { e.preventDefault(); setSa((a) => (e.key === "ArrowDown" ? Math.min(a + 1, sug.length - 1) : Math.max(a - 1, 0))); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && sa >= 0 && sug[sa]) { e.preventDefault(); fill(sug[sa]); }
    else if (e.key === "Escape") setOpen(false);
    else if (e.key === "Enter") {
      e.preventDefault();
      const code = q.trim();
      if (!code) return;
      if (onEnterExact) {
        const exact = await onEnterExact(code);
        if (exact) return pick(exact);
      }
      const list = items.length ? items : await fetcher(code);
      if (list[active]) pick(list[active]);
    }
  };
  return (
    <div className="relative" data-testid={testid}>
      <input ref={ref} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onKeyDown={onKey} onFocus={() => { clearTimeout(blurT.current); setOpen(true); }} onBlur={() => { blurT.current = setTimeout(() => setOpen(false), 150); }}
        placeholder={placeholder} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600" data-testid={`${testid}-input`} />
      {open && sug.length > 0 && items.length === 0 && <SuggestList items={sug} active={sa} onPick={fill} testid={testid} />}
      {open && items.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-11 bg-white border border-slate-200 rounded-md shadow-xl max-h-80 overflow-y-auto" data-testid={`${testid}-results`}>
          {sug.length > 0 && <div className="flex flex-wrap gap-1 border-b border-slate-100 bg-slate-50 px-2 py-1.5" data-testid={`${testid}-suggest`}>
            <span className="self-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Öneriler</span>
            {sug.map((s, i) => <button key={s.text} type="button" onMouseDown={(e) => { e.preventDefault(); fill(s); }} data-testid={`${testid}-suggest-${i}`}
              className={`rounded-full border px-2 py-0.5 text-[12px] transition-colors ${i === sa ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-400"}`}>{s.text} <span className="text-slate-400">{s.count}</span></button>)}
          </div>}
          {items.map((it, i) => (
            <button key={it.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(it); }} data-testid={`${testid}-item-${i}`}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 ${i === active ? "bg-blue-50" : "hover:bg-slate-50"}`}>{render(it)}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export const fetchCustomers = (q) => api.get("/customers", { params: { q, page_size: 8 } }).then(({ data }) => data.items);
export const fetchProducts = (q) => api.get("/products", { params: { q, page_size: 8 } }).then(({ data }) => data.items);
